import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import api from '../services/api';
import { useZReports } from '../hooks/useZReports';
import ZReportList from '../components/ZReports/ZReportList';
import ZReportForm from '../components/ZReports/ZReportForm';
import ZReportDetail from '../components/ZReports/ZReportDetail';
import { TableCard } from '../components/ui';
import '../styles/zreports.css';

const MySwal = withReactContent(Swal);

function ZReportsPage() {
  const [showForm, setShowForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: ''
  });
  const navigate = useNavigate();

  // Usar el hook personalizado para paginación
  const {
    reports,
    pagination,
    loading,
    error,
    goToPage,
    nextPage,
    prevPage,
    changePerPage,
  } = useZReports({
    startDate: filters.start_date,
    endDate: filters.end_date,
    perPage: 20,
  });

  // Mostrar error si existe
  if (error) {
    MySwal.fire({
      icon: 'error',
      title: 'Error',
      text: error,
      confirmButtonText: 'Entendido'
    });
  }

  const handleGenerateReport = async (reportData) => {
    try {
      const response = await api.post('/zreports', reportData);
      
      if (response.data.type === 'new') {
        MySwal.fire({
          icon: 'success',
          title: 'Reporte generado',
          text: response.data.message,
        });
      } else {
        MySwal.fire({
          icon: 'info',
          title: 'Reporte existente',
          text: response.data.message,
        });
      }
      
      setShowForm(false);
      // Recargar la primera página después de generar un reporte
      goToPage(1);
    } catch (error) {
      console.error('Error generating report:', error);
      
      // Extraer mensaje de error del servidor
      let errorMessage = 'No se pudo generar el reporte Z.';
      
      if (error.response) {
        const { status, data } = error.response;
        
        // Si hay un mensaje principal del servidor
        if (data.message) {
          errorMessage = data.message;
        }
        
        // Si hay errores específicos, agregarlos al mensaje
        if (data.errors) {
          const errorDetails = [];
          
          // Procesar diferentes tipos de errores
          Object.keys(data.errors).forEach(key => {
            const errors = data.errors[key];
            if (Array.isArray(errors)) {
              errors.forEach(err => errorDetails.push(err));
            } else if (typeof errors === 'string') {
              errorDetails.push(errors);
            }
          });
          
          if (errorDetails.length > 0) {
            errorMessage = `${errorMessage}\n\n${errorDetails.join('\n')}`;
          }
        }
        
        // Mostrar diferentes iconos según el tipo de error
        let icon = 'error';
        if (status === 422) {
          icon = 'warning'; // Error de validación
        } else if (status === 400) {
          icon = 'warning'; // Bad request
        } else if (status === 403) {
          icon = 'error'; // Forbidden
        } else if (status === 500) {
          icon = 'error'; // Server error
        }
        
        MySwal.fire({
          icon: icon,
          title: 'Error al generar reporte',
          text: errorMessage,
          confirmButtonText: 'Entendido'
        });
      } else if (error.request) {
        // Error de red
        MySwal.fire({
          icon: 'error',
          title: 'Error de conexión',
          text: 'No se pudo conectar con el servidor. Verifica tu conexión a internet.',
          confirmButtonText: 'Entendido'
        });
      } else {
        // Error general
        MySwal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          confirmButtonText: 'Entendido'
        });
      }
    }
  };

  const handleDownloadReport = async (reportId) => {
    console.log('reportId', reportId);
    try {
      const response = await api.get(`/zreports/${reportId}/file`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte-z-${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
      
      let errorMessage = 'No se pudo descargar el reporte.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonText: 'Entendido'
      });
    }
  };

  const handleDeleteReport = async (reportId) => {
    const result = await MySwal.fire({
      icon: 'warning',
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer.',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/zreports/${reportId}`);
        MySwal.fire({
          icon: 'success',
          title: 'Eliminado',
          text: 'El reporte ha sido eliminado.',
        });
        // Recargar la página actual después de eliminar
        goToPage(pagination?.current_page || 1);
      } catch (error) {
        console.error('Error deleting report:', error);
        
        let errorMessage = 'No se pudo eliminar el reporte.';
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        MySwal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          confirmButtonText: 'Entendido'
        });
      }
    }
  };

  const handleViewReport = async (reportId) => {
    try {
      const response = await api.get(`/zreports/${reportId}`);
      setSelectedReport(response.data);
      setShowDetail(true);
    } catch (error) {
      console.error('Error loading report detail:', error);
      
      let errorMessage = 'No se pudo cargar el detalle del reporte.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonText: 'Entendido'
      });
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Los filtros se aplicarán automáticamente por el hook
  };

  const handlePageChange = (page) => {
    goToPage(page);
  };

  const handlePerPageChange = (perPage) => {
    // Cambiar elementos por página usando el hook
    changePerPage(perPage);
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <ZReportList
            reports={reports}
            pagination={pagination}
            loading={loading}
            onDownload={handleDownloadReport}
            onDelete={handleDeleteReport}
            onView={handleViewReport}
            onFilterChange={handleFilterChange}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            filters={filters}
            renderLayout={({ toolbar, table, pagination }) => (
              <TableCard
                header={
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <h4 className="card-title mb-0">Reportes Z</h4>
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowForm(true)}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Generar Reporte Z
                    </button>
                  </div>
                }
                toolbar={toolbar}
                table={table}
                pagination={pagination}
              />
            )}
          />
        </div>
      </div>

      {/* Modal para generar reporte */}
      {showForm && (
        <ZReportForm
          onClose={() => setShowForm(false)}
          onSubmit={handleGenerateReport}
        />
      )}

      {/* Modal para ver detalle del reporte */}
      {showDetail && selectedReport && (
        <ZReportDetail
          report={selectedReport}
          onClose={() => {
            setShowDetail(false);
            setSelectedReport(null);
          }}
          onDownload={handleDownloadReport}
        />
      )}
    </div>
  );
}

export default ZReportsPage; 